// Schedule's views' props, as zod (pages/ux-full components/schedule.js: WeekGrid, TeamList, TechDay,
// TimeOffList, and schedule/map's DayMap). Each is drawn from ready-to-show words that the office builds
// from real answers (dispatch.board, capacity.availability, staff.list, timeOff.list), named beside each
// schema. No one
// tool returns any of these yet, so they aren't in TOOL_VIEWS: a tool that does (a chat's "who's
// working") registers it there with the same props.
import { z } from 'zod';

const Id = z.string().min(1);
const LocalDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
/** A technician's pen, 1 to 8 (--bp-pen-N): how Blueprint tells them apart. */
const Pen = z.int().min(1).max(8).nullable();

/** The week by technician: dispatch.board (from, to), capacity.availability's techOpenMinutes. */
export const WeekGridProps = z.object({
  days: z.array(
    z.object({
      date: LocalDate,
      /** "Mon 26" */
      label: z.string(),
      today: z.boolean(),
      /** Visits that day nobody is on yet (dispatch.board's unassigned). */
      waiting: z.int().min(0),
    }),
  ),
  techs: z.array(
    z.object({
      id: Id,
      name: z.string(),
      pen: Pen,
      /** One a day, in the days' order. */
      cells: z.array(
        z.object({
          date: LocalDate,
          visits: z.int().min(0),
          bookedMinutes: z.int().min(0),
          /**
           * Their working minutes that day (their own hours, their department's, else the company's);
           * 0 is a day off. Null when those hours are the whole day (open round the clock): no shift, so
           * the cell says what's booked against no figure.
           */
          workingMinutes: z.int().min(0).nullable(),
          /** Free working minutes (hours less time away and visits); null when not known. */
          openMinutes: z.int().min(0).nullable(),
          /** All-day time off or a holiday: its name ("Vacation"). */
          away: z.string().nullable(),
        }),
      ),
    }),
  ),
});
export type WeekGridProps = z.infer<typeof WeekGridProps>;

export const TEAM_STATES = ['working', 'driving', 'away', 'free', 'off'] as const;

/** Who's working today: today's dispatch.board, with staff.list's skills. */
export const TeamListProps = z.object({
  techs: z.array(
    z.object({
      id: Id,
      name: z.string(),
      pen: Pen,
      /** On a job (on site), on the way (dispatched), away (non-job time, part-day time off), free, off. */
      state: z.enum(TEAM_STATES),
      /** The state in words: "On a job", "Lunch", "Off today". */
      word: z.string(),
      /** What it is: "Nguyen residence, since 9:02 a.m.", "Vacation, Thu, Oct 29 to Fri, Oct 30". */
      line: z.string(),
      /** Running late or long, in words ("Running long 20 min"): a problem, drawn red. */
      late: z.string().nullable(),
      visits: z.int().min(0),
      bookedMinutes: z.int().min(0),
      skills: z.array(z.string()),
    }),
  ),
});
export type TeamListProps = z.infer<typeof TeamListProps>;

/** One technician's day: their visits from dispatch.board, its open time and what takes them away. */
export const TechDayProps = z.object({
  name: z.string(),
  pen: Pen,
  /** "Today, Friday, October 23" */
  date: z.string(),
  /** Their working hours in words ("8 a.m.–4:30 p.m."); null on a day off. */
  hours: z.string().nullable(),
  bookedMinutes: z.int().min(0),
  openMinutes: z.int().min(0),
  /** Off all day: why ("Vacation", "Not working today"); then there are no stops. */
  off: z.string().nullable(),
  stops: z.array(
    z.object({
      kind: z.enum(['visit', 'open', 'away']),
      /** Clock words: "9 a.m.", "11:15 a.m." */
      start: z.string(),
      end: z.string(),
      /** A visit's customer, or what takes them away ("Lunch", "Appointment"). */
      title: z.string(),
      /** A visit's job type and address, in words. */
      detail: z.string().nullable(),
      state: z.enum(['done', 'paused', 'now', 'next']).nullable(),
      late: z.string().nullable(),
    }),
  ),
});
export type TechDayProps = z.infer<typeof TechDayProps>;

/** timeOff.list's answer, with each person's pen. */
export const TimeOffListProps = z.object({
  items: z.array(
    z.object({
      id: Id,
      name: z.string(),
      pen: Pen,
      /** The leave code's name: "Vacation". */
      label: z.string(),
      /** In words: "Vacation, Thu, Oct 29 to Fri, Oct 30". */
      text: z.string(),
      paid: z.boolean(),
      note: z.string().nullable(),
      cancelled: z.object({ reason: z.string() }).nullable(),
    }),
  ),
});
export type TimeOffListProps = z.infer<typeof TimeOffListProps>;

export const MAP_PIN_STATES = ['done', 'now', 'booked', 'waiting'] as const;
const Lat = z.number().min(-90).max(90);
const Lng = z.number().min(-180).max(180);

/**
 * A day's visits on the map: dispatch.board's visits with their address's point (geo, ServiceTitan's
 * latitude and longitude), each technician's route in their pen, and the visits whose address has no
 * point, listed, never dropped. The map is drawn from the points alone: no tiles, no map service.
 */
export const DayMapProps = z.object({
  pins: z.array(
    z.object({
      /** The visit (its appointment). */
      id: Id,
      lat: Lat,
      lng: Lng,
      /** The pin's label: the customer's first word ("Fairview"). */
      label: z.string(),
      /** What a screen reader says for it: "Fairview residence, 9–11 a.m., Sam R., working now". */
      title: z.string(),
      /** Its town, as the address gives it ("Saanich"): the map names each where its visits are. */
      area: z.string().nullable(),
      /** Done (closed), now (on site), booked (to come, or on the way), waiting (nobody on it yet). */
      state: z.enum(MAP_PIN_STATES),
      /** Running late or long, or at risk: a problem, drawn red. */
      late: z.boolean(),
      /** The lead technician's pen; null with nobody on it. */
      pen: Pen,
    }),
  ),
  /** Each technician's visits in time order, by pin id: their route, in their pen. */
  routes: z.array(z.object({ id: Id, pen: Pen, stops: z.array(Id) })),
  /** The shop, where its address has a point; null when it has none. */
  shop: z.object({ lat: Lat, lng: Lng, label: z.string() }).nullable(),
  /** The day's visits whose address has no point: listed beside the map. */
  unplaced: z.array(
    z.object({
      id: Id,
      /** The customer. */
      title: z.string(),
      /** When and who: "9–11 a.m., Sam R." */
      line: z.string(),
      /** Its address in words. */
      address: z.string(),
    }),
  ),
});
export type DayMapProps = z.infer<typeof DayMapProps>;
